import { createRouter, createWebHistory } from 'vue-router';
import SmsCodeVerification from '@/features/SmsCodeVerification/pages/SmsCodeVerification.vue';
import MainLayout from '@/layout/MainLayout/MainLayout.vue';
import LoggedLayout from '@/layout/LoggedLayout/LoggedLayout.vue';
import { useAuth } from '@/composables/useAuth';

export type RouteMeta = {
  layout: typeof MainLayout;
  requiresAuth: boolean;
  hideDuringAuth: boolean;
};

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'SmsCodeVerification',
      component: SmsCodeVerification,
      meta: {
        layout: MainLayout,
        requiresAuth: false,
        hideDuringAuth: true,
      },
    },
    {
      path: '/sign-in',
      name: 'SignIn',
      component: () => import('@/features/SignIn/pages/SignIn.vue'),
      meta: {
        layout: MainLayout,
        requiresAuth: false,
        hideDuringAuth: true,
      },
    },
    {
      path: '/emergency-contacts',
      meta: { layout: LoggedLayout, requiresAuth: true, hideDuringAuth: false },
      component: () =>
        import('@/features/EmergencyContact/page/EmergencyContactPage.vue'),
    },
    {
      path: '/devices',
      meta: { layout: LoggedLayout, requiresAuth: true, hideDuringAuth: false },
      component: () => import('@/features/Devices/pages/DevicesPage.vue'),
    },
    {
      path: '/devices/:serialNumber',
      meta: { layout: LoggedLayout, requiresAuth: true, hideDuringAuth: false },
      component: () => import('@/features/Devices/pages/DeviceDetailsPage.vue'),
    },
    {
      path: '/settings',
      meta: { layout: LoggedLayout, requiresAuth: true, hideDuringAuth: false },
      component: () => import('@/features/Settings/pages/SettingsPage.vue'),
    },
    {
      path: '/settings/api-keys/create',
      meta: { layout: LoggedLayout, requiresAuth: true, hideDuringAuth: false },
      component: () => import('@/features/Settings/pages/CreateApiKeyPage.vue'),
    },
    {
      path: '/settings/api-keys/:id',
      meta: { layout: LoggedLayout, requiresAuth: true, hideDuringAuth: false },
      component: () => import('@/features/Settings/pages/EditApiKeyPage.vue'),
    },
    {
      path: '/no-emergency-contact',
      meta: { layout: LoggedLayout, requiresAuth: true, hideDuringAuth: false },
      component: () =>
        import('@/features/NoEmergencyContact/page/NoEmergencyContact.vue'),
    },
    {
      path: '/not-found',
      meta: { layout: MainLayout, requiresAuth: false, hideDuringAuth: false },
      component: () => import('@/features/StatusPages/pages/NotFound.vue'),
    },
    {
      path: '/activate',
      meta: { layout: MainLayout, requiresAuth: false, hideDuringAuth: false },
      component: () =>
        import('@/features/DeviceActivation/pages/DeviceActivatePage.vue'),
    },
    {
      path: '/activate/success',
      meta: { layout: MainLayout, requiresAuth: false, hideDuringAuth: false },
      component: () =>
        import('@/features/DeviceActivation/pages/DeviceActivateSuccess.vue'),
    },
    {
      path: '/activated',
      meta: { layout: MainLayout, requiresAuth: false, hideDuringAuth: false },
      component: () =>
        import('@/features/DeviceActivation/pages/DeviceAlreadyActivatedPage.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      meta: { layout: MainLayout, requiresAuth: false },
      component: () => import('@/features/StatusPages/pages/NotFound.vue'),
    },
  ],
});

router.beforeResolve(async (to, from, next) => {
  const auth = useAuth();
  const meta = to.meta as RouteMeta;

  if (!auth.initiallyCheckedLogin.value) {
    await auth.checkIfAuth();
  }

  if (meta.requiresAuth && !auth.isLoggedIn.value) {
    next(`/`);
    return;
  }

  if (meta.hideDuringAuth && auth.isLoggedIn.value) {
    next(!auth.isEmergencyContact() ? '/devices' : '/emergency-contacts');

    return;
  }

  next();
});

export default router;
